import AppLayout from '@/layout/AppLayout.vue';
import { useAuthStore } from '@/store/auth-store';
import { useMenuStore } from '@/store/menu-store';
import { useRouterStore } from '@/store/router-store';
import { markRaw } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';

const modules = import.meta.glob('@/views/pages/**/*.vue');

export async function loadComponent(componentPath) {
    if (componentPath === 'dashboard') {
        const module = await import('@/views/dashboard.vue');
        return { ...module.default, name: 'dashboard' };
    }
    const path = `/src/views/pages/${componentPath}.vue`;
    if (modules[path]) {
        const module = await modules[path]();
        return { ...module.default, name: module.default.name || componentPath };
    } else {
        console.error(`Component not found: ${path}`);
        const errorModule = await import('@/views/pages/auth/Error.vue');
        return { ...errorModule.default, name: 'Error' };
    }
}

export async function addDynamicRoutes(router, routes) {
    //const existingRoutes = router.getRoutes();
    //const routeNamesToKeep = ['main', 'dashboard'];
    //const routerStore = useRouterStore();
    const routesToKeep = ['main', 'dashboard', 'login', 'NotFound', 'privacytreat'];
    router.getRoutes().forEach((route) => {
        if (!routesToKeep.includes(route.name)) {
            router.removeRoute(route.name);
        }
    });

    for (const route of routes) {
        if (!router.hasRoute(route.name)) {
            const component = await loadComponent(route.componentPath);
            router.addRoute('main', {
                path: route.path,
                name: route.name,
                component: markRaw(component),
                meta: {
                    ...route.meta,
                    keepAlive: true,
                    componentName: component.name
                }
            });
        }
    }

    if (!router.hasRoute('NotFound')) {
        router.addRoute({
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            component: () => import('@/views/pages/auth/Error.vue'),
            meta: { title: 'Not Found', public: true }
        });
    }

    // const currentRoute = router.currentRoute.value;
    // if (router.hasRoute(currentRoute.name)) {
    //     //routerStore.setCurrentPath(currentRoute.path);
    //     await router.replace({ name: currentRoute.name });
    // } else {
    //     console.warn('현재 경로가 유효하지 않아 dashboard로 이동합니다.');
    //     await router.replace({ name: 'dashboard' });
    // }

    console.log('regist router : ', router.getRoutes().length, ' pages');
    // await router.replace(router.currentRoute.value.fullPath);
    // console.log('regist router : ', router.getRoutes());
}

const router = createRouter({
    history: createWebHistory(),
    routes: [
        // 메인 레이아웃과 대시보드
        {
            path: '/',
            name: 'main',
            component: markRaw(AppLayout),
            meta: { requiresAuth: true },
            children: [
                {
                    path: '',
                    name: 'dashboard',
                    meta: {
                        title: '대시보드',
                        keepAlive: true,
                        requiresAuth: true
                    },
                    component: markRaw(() => import('@/views/dashboard.vue'))
                }
            ]
        },

        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/pages/login.vue'),
            meta: {
                title: '로그인',
                public: true,
                requiresAuth: false
            }
        },
        {
            path: '/privacytreat',
            name: 'privacytreat',
            component: () => import('@/views/pages/privacytreat.vue'),
            meta: {
                title: '개인정보처리방침',
                public: true,
                requiresAuth: false
            }
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            component: () => import('@/views/pages/auth/Error.vue'),
            meta: {
                title: 'Not Found',
                public: true,
                requiresAuth: false
            }
        }
    ]
});

router.beforeEach(async (to, from, next) => {
    //console.log(`이동 중: ${to.name}, 경로: ${to.path}`);

    const routerStore = useRouterStore();
    const menuStore = useMenuStore();
    const authStore = useAuthStore();

    const isAuthenticated = authStore.checkAuth();

    if (to.meta.requiresAuth && !isAuthenticated) {
        console.log('로그인 후 이용할 수 있습니다.'); //  요청 경로:', to.path
        return next('/login');
    }

    if (to.name === 'login' && isAuthenticated) {
        console.log('이미 로그인했습니다. 대시보드로 이동합니다.');
        return next('/');
    }

    // public 페이지 체크
    if (to.meta.public) {
        //console.log('퍼블릭 페이지 접근 허용:', to.path);
        return next();
    }

    // 매칭되는 라우트가 없는 경우 처리
    if (to.matched.length === 0) {
        console.error('해당 라우트가 존재하지 않습니다:', to.fullPath);
        return next({ name: 'NotFound' });
    }

    // 대시보드나 로그인 페이지로 이동할 때 탭 초기화
    if (to.path === '/' || to.path === '/login') {
        menuStore.clearTabs();
    }

    if (!routerStore.isInitialized && !to.meta.public) {
        try {
            //console.log('beforeeach');
            menuStore.clearTabs();
            await routerStore.fetchRouterItems();
            await addDynamicRoutes(router, routerStore.routerList);
            routerStore.setInitialized(true);
            return next(to.fullPath);
        } catch (error) {
            console.error('라우트 추가 실패:', error);
            return next('/error');
        }
    }

    //localStorage.setItem('currentPath', to.fullPath);

    if (to.matched.length === 0) {
        console.error('해당 라우트가 존재하지 않습니다:', to.fullPath);
        router.push({ name: 'NotFound' });
    }
    if (to.meta && to.meta.title) {
        routerStore.setCurrentTitle(to.meta.title);
    }
    routerStore.setCurrentPath(to.path);

    if (to.name !== 'dashboard') {
        const tab = {
            menu_item_code: to.name,
            menu_item_name: to.meta.title,
            componentPath: to.meta.componentPath,
            componentName: to.meta.componentName
        };
        menuStore.addTab(tab);
    }

    if (to.name === from.name && JSON.stringify(to.params) === JSON.stringify(from.params)) {
        return next(false);
    }

    next();
});

export default router;
